import { useTranslation } from 'react-i18next';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

export interface GymInfoValues {
  websiteUrl: string;
  address: string;
  city: string;
  zipcode: string;
  latitude: string;
  longitude: string;
}

interface GymInfoFieldsProps {
  values: GymInfoValues;
  onChange: (patch: Partial<GymInfoValues>) => void;
  /**
   * Leaves the latitude/longitude pair out. For a crag, which has a second pair
   * for the parking: the two are read against each other and are edited
   * together further down the page, so one of them here would be a trap.
   */
  hideCoordinates?: boolean;
}

/**
 * Controlled inputs for a gym's contact/location metadata (website, address,
 * city, zipcode, coordinates). Shared between the gym settings page and the
 * gym-admin Info page. The parent owns state and persistence.
 */
export function GymInfoFields({ values, onChange, hideCoordinates = false }: GymInfoFieldsProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      {/* Website */}
      <div className="space-y-2">
        <Label htmlFor="websiteUrl">{t('gymSettings.websiteUrl')}</Label>
        <Input
          id="websiteUrl"
          type="url"
          placeholder={t('gymSettings.websiteUrlPlaceholder')}
          value={values.websiteUrl}
          onChange={(e) => onChange({ websiteUrl: e.target.value })}
        />
      </div>

      {/* Address row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="sm:col-span-2 space-y-2">
          <Label htmlFor="address">{t('gymSettings.address')}</Label>
          <Input
            id="address"
            value={values.address}
            onChange={(e) => onChange({ address: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="city">{t('gymSettings.city')}</Label>
          <Input
            id="city"
            value={values.city}
            onChange={(e) => onChange({ city: e.target.value })}
          />
        </div>
      </div>

      {/* Zipcode + Coordinates */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="zipcode">{t('gymSettings.zipcode')}</Label>
          <Input
            id="zipcode"
            value={values.zipcode}
            onChange={(e) => onChange({ zipcode: e.target.value })}
          />
        </div>
        {!hideCoordinates && (
          <>
            <div className="space-y-2">
              <Label htmlFor="latitude">{t('gymSettings.latitude')}</Label>
              <Input
                id="latitude"
                type="number"
                step="any"
                min="-90"
                max="90"
                value={values.latitude}
                onChange={(e) => onChange({ latitude: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="longitude">{t('gymSettings.longitude')}</Label>
              <Input
                id="longitude"
                type="number"
                step="any"
                min="-180"
                max="180"
                value={values.longitude}
                onChange={(e) => onChange({ longitude: e.target.value })}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
